import { UserInfo } from '@msi/commandcentral-common-header';
import { ViewModel } from 'src/app/interfaces/views.interface';
import { environment as env } from 'src/environments/environment';

export enum OrchPerms {
	agencyWorkflowEdit = 'agency.workflow.edit',
	agencyWorkflowStatus = 'agency.workflow.status',
	agencyWorkspaceAssign = 'agency.workspace.assign',
	agencyWorkspaceDelete = 'agency.workspace.delete',
	agencyWorkspaceEdit = 'agency.workspace.edit',
	agencyWorkspaceView = 'agency.workspace.view',
	capabilityRename = 'capability.rename',
	thresholdEdit = 'threshold.edit',
	urgentNotifications = 'notifications.urgent',
	view = 'view',
	workflowEdit = 'workflow.edit',
	workflowStatus = 'workflow.status',
	workspaceAssign = 'workspace.assign',
	workspaceEdit = 'workspace.edit',
	configureConnections = 'connections.edit',
	configurePublicPrivatePartnership = 'ppp.edit',
}

export enum OrchDemoPerms {
	research = 'research_user',
	partner = 'partner_user', // (not in use)
}

const testingPermissionsLocally = false; // Set to true if testing permissions locally (This will break some unit tests, so it cannot be committed)
const isDemo = env.name === 'demo' || env.name === 'demoProd' || (env.name === 'local' && !testingPermissionsLocally);
const isLocal = env.name === 'local';

export class UserPermissions {
	private userInfo: UserInfo;
	private permissions = new Set<string>();

	constructor(userInfo: UserInfo, permissionsList: string[]) {
		this.userInfo = userInfo;
		this.permissions = new Set<string>(permissionsList);
	}

	public get userId() {
		return this.userInfo.email;
	}

	public get agencyId() {
		return this.userInfo.agency.id;
	}

	public get canConfigDemo() {
		return isLocal || this.permissions.has(OrchDemoPerms.research);
	}

	public get canDeleteAnyWorkspace() {
		return isDemo || this.permissions.has(OrchPerms.agencyWorkspaceDelete);
	}

	public get canEditAnyWorkspace() {
		return isDemo || this.permissions.has(OrchPerms.agencyWorkspaceEdit);
	}

	public get canViewAnyWorkspace() {
		return (
			isDemo ||
			this.permissions.has(OrchPerms.agencyWorkspaceView) ||
			this.permissions.has(OrchPerms.agencyWorkflowEdit) ||
			this.permissions.has(OrchPerms.agencyWorkflowStatus) ||
			this.permissions.has(OrchPerms.agencyWorkspaceAssign) ||
			this.permissions.has(OrchPerms.agencyWorkspaceDelete) ||
			this.permissions.has(OrchPerms.agencyWorkspaceEdit)
		);
	}

	public canViewWorkspace(workspace: ViewModel) {
		const isInWorkspaceAcl = workspace.aclUsers.some(x => x.email === this.userInfo.email);
		return isDemo || this.canViewAnyWorkspace || (this.canView && isInWorkspaceAcl);
	}

	public get canAssignAnyWorkspace() {
		return isDemo || this.permissions.has(OrchPerms.agencyWorkspaceAssign);
	}

	public get canEditAnyWorkflow() {
		return isDemo || this.permissions.has(OrchPerms.agencyWorkflowEdit);
	}

	public get canToggleAnyWorkflow() {
		return (
			isDemo ||
			this.permissions.has(OrchPerms.agencyWorkflowStatus) ||
			this.permissions.has(OrchPerms.agencyWorkflowEdit)
		);
	}

	public get canRenameCapabilities() {
		return isDemo || this.permissions.has(OrchPerms.capabilityRename);
	}

	public get canEditThresholds() {
		return isDemo || this.permissions.has(OrchPerms.thresholdEdit);
	}

	public get receivesUrgentNotifications() {
		return isDemo || this.permissions.has(OrchPerms.urgentNotifications);
	}

	public get canView() {
		return isDemo || this.permissions.has(OrchPerms.view);
	}

	public get canEditWorkflows() {
		return isDemo || this.permissions.has(OrchPerms.workflowEdit);
	}

	public get canToggleWorkflows() {
		return isDemo || this.permissions.has(OrchPerms.workflowStatus) || this.permissions.has(OrchPerms.workflowEdit);
	}

	public get canAssignWorkspaces() {
		return isDemo || this.permissions.has(OrchPerms.workspaceAssign);
	}

	public get canEditWorkspaces() {
		return isDemo || this.permissions.has(OrchPerms.workspaceEdit);
	}

	public get canConfigureConnections() {
		return this.permissions.has(OrchPerms.configureConnections);
	}

	// TODO: Use or delete this
	public get canConfigurePPP() {
		return isDemo || this.permissions.has(OrchPerms.configurePublicPrivatePartnership);
	}

	public canAssignWorkspace(workspace: ViewModel) {
		const isInWorkspaceAcl = this.isUserInAcl(workspace);
		return isDemo || this.canAssignAnyWorkspace || (this.canAssignWorkspaces && isInWorkspaceAcl);
	}

	public canEditWorkspace(workspace: ViewModel) {
		const isInWorkspaceAcl = this.isUserInAcl(workspace);
		return isDemo || this.canEditAnyWorkspace || (this.canEditWorkspaces && isInWorkspaceAcl);
	}

	public canDeleteWorkspace() {
		return isDemo || this.canDeleteAnyWorkspace;
	}

	public canEditWorkflowsInWorkspace(workspace: ViewModel) {
		if (!workspace || workspace?.id === 'show-all') {
			return this.canEditAnyWorkflow;
		}
		const isInWorkspaceAcl = this.isUserInAcl(workspace);
		return isDemo || this.canEditAnyWorkflow || (this.canEditWorkflows && isInWorkspaceAcl);
	}

	public canToggleWorkflowsInWorkspace(workspace: ViewModel) {
		if (!workspace) {
			return this.canToggleAnyWorkflow;
		}
		const isInWorkspaceAcl = this.isUserInAcl(workspace);
		return isDemo || this.canToggleAnyWorkflow || (this.canToggleWorkflows && isInWorkspaceAcl);
	}

	private isUserInAcl(workspace: ViewModel) {
		return !workspace.aclEnabled || workspace.aclUsers.some(x => x.email === this.userInfo.email);
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
